import { normalizeStage } from './status';

const CLOSED_STAGES = new Set(['archived', 'delivered', 'cancelled', 'canceled', 'complete', 'completed']);
const DRIVER_WAIT_STAGES = new Set(['new', 'waiting', 'awaiting_store', 'packing', 'preparing', 'ready', 'ready_for_driver', 'driver_assigned']);
const ROUTE_STAGES = new Set(['out_for_delivery', 'on_route', 'out for delivery']);

const SLA_TARGETS = {
  new: 35,
  waiting: 35,
  awaiting_store: 35,
  packing: 25,
  preparing: 25,
  ready: 20,
  ready_for_driver: 20,
  driver_assigned: 18,
  collected: 25,
  out_for_delivery: 45,
  on_route: 45,
  issue: 5,
  paused: 60,
};

function safeArray(value) {
  return Array.isArray(value) ? value.filter(Boolean) : [];
}

function firstText(...values) {
  return values.map((value) => String(value || '').trim()).find(Boolean) || '';
}

function asMillis(value) {
  if (!value) return 0;
  if (value instanceof Date) return value.getTime();
  if (typeof value?.toDate === 'function') return value.toDate().getTime();
  if (typeof value === 'number') return value;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function minutesSince(value, now = Date.now()) {
  const millis = asMillis(value);
  if (!millis) return 0;
  return Math.max(0, Math.floor((now - millis) / 60000));
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function orderStage(order = {}) {
  order = order && typeof order === 'object' ? order : {};
  order = order && typeof order === 'object' ? order : {};
  return normalizeStage(order.stage || order.status || order.subStatus || order.storeStatus || 'new');
}

function orderCreatedAt(order = {}) {
  order = order && typeof order === 'object' ? order : {};
  const raw = order.raw || {};
  const timings = order.timings || raw.timings || {};
  return order.createdAt || order.placedAt || timings.createdAt || raw.createdAt || raw.placedAt || order.updatedAt || raw.updatedAt;
}

function stageStartedAt(order = {}) {
  order = order && typeof order === 'object' ? order : {};
  order = order && typeof order === 'object' ? order : {};
  const raw = order.raw || {};
  const timings = order.timings || raw.timings || {};
  return (
    timings.stageStartedAt ||
    timings.readyForDriverAt ||
    timings.readyAt ||
    timings.packingStartedAt ||
    timings.packedAt ||
    timings.sentToStoreAt ||
    timings.routedAt ||
    order.updatedAt ||
    raw.updatedAt ||
    orderCreatedAt(order)
  );
}

function assignedDriverName(order = {}) {
  order = order && typeof order === 'object' ? order : {};
  order = order && typeof order === 'object' ? order : {};
  const raw = order.raw || {};
  return firstText(order.assignedDriverName, order.assignedDriver, order.driverName, order.assignment?.driverName, raw.assignedDriverName, raw.assignment?.driverName);
}

function isClosed(order = {}) {
  const stage = orderStage(order);
  return Boolean(order.archived || order.cancelled || order.delivered || CLOSED_STAGES.has(stage));
}

function hasDriverRisk(order = {}) {
  return DRIVER_WAIT_STAGES.has(orderStage(order)) && !assignedDriverName(order);
}

function stageTargetMinutes(stage) {
  return SLA_TARGETS[stage] ?? 35;
}

function riskFromRemaining(minutesToBreach, stageMinutes, targetMinutes) {
  if (minutesToBreach <= 0) return 'critical';
  if (minutesToBreach <= 10) return 'critical';
  if (minutesToBreach <= 20 || stageMinutes >= targetMinutes * 0.65) return 'attention';
  return 'healthy';
}

function trendFromSignal({ minutesToBreach, stageMinutes, targetMinutes, hasDriverIssue, storeLoad = 0 }) {
  if (minutesToBreach <= 10 || hasDriverIssue || storeLoad >= 70) return 'rising';
  if (stageMinutes < targetMinutes * 0.35 && storeLoad < 45) return 'stable';
  return 'watching';
}

function formatWindow(minutesToBreach) {
  if (minutesToBreach <= 0) return 'Breached';
  return `${minutesToBreach}m`;
}

export function predictLinkSla(order = {}, options = {}) {
  const now = options.now || Date.now();
  const stage = orderStage(order);
  const stageMinutes = minutesSince(stageStartedAt(order), now);
  const ageMinutes = minutesSince(orderCreatedAt(order), now);
  const targetMinutes = Number(options.targetMinutes || stageTargetMinutes(stage));
  const minutesToBreach = Math.floor(targetMinutes - stageMinutes);
  const storeSignal = options.storeSignal || {};
  const storeLoad = Number(storeSignal.loadScore ?? storeSignal.riskScore ?? 0) || 0;
  const driverSignals = safeArray(options.driverSignals);
  const availableDrivers = driverSignals.filter((driver) => driver.available || (driver.online && Number(driver.activeRuns || 0) === 0)).length;
  const driverRisk = hasDriverRisk(order);
  const routeRisk = ROUTE_STAGES.has(stage) && stageMinutes >= 30;
  const currentRisk = isClosed(order) ? 'closed' : riskFromRemaining(minutesToBreach, stageMinutes, targetMinutes);
  const predictedRisk = isClosed(order)
    ? 'closed'
    : riskFromRemaining(minutesToBreach - (driverRisk ? 8 : 0) - (storeLoad >= 70 ? 6 : 0), stageMinutes + 8, targetMinutes);

  const overduePenalty = Math.max(0, -minutesToBreach) * 2;
  const pressurePenalty = storeLoad >= 70 ? 16 : storeLoad >= 45 ? 8 : 0;
  const driverPenalty = driverRisk ? 18 : 0;
  const routePenalty = routeRisk ? 12 : 0;
  const driverBoost = Math.min(18, availableDrivers * 6);
  const recoveryChance = clamp(Math.round(82 - overduePenalty - pressurePenalty - driverPenalty - routePenalty + driverBoost), 8, 96);

  let recommendedFocus = 'monitor';
  if (driverRisk) recommendedFocus = 'assign_driver';
  else if (storeLoad >= 70) recommendedFocus = 'move_store';
  else if (predictedRisk === 'critical') recommendedFocus = 'intervene';

  return {
    orderId: order.id,
    stage,
    targetMinutes,
    ageMinutes,
    stageMinutes,
    minutesToBreach,
    breachLabel: formatWindow(minutesToBreach),
    currentRisk,
    predictedRisk,
    riskTrend: trendFromSignal({ minutesToBreach, stageMinutes, targetMinutes, hasDriverIssue: driverRisk, storeLoad }),
    recoveryChance,
    availableDrivers,
    storeLoad,
    driverRisk,
    routeRisk,
    recommendedFocus,
    summary: minutesToBreach <= 0
      ? `SLA already breached by ${Math.abs(minutesToBreach)}m`
      : `SLA breach predicted in ${minutesToBreach}m`,
  };
}

export function buildLinkPredictionMap(orders = [], options = {}) {
  const storeSignals = safeArray(options.storeSignals);
  const driverSignals = safeArray(options.driverSignals);
  const map = new Map();

  safeArray(orders).forEach((order) => {
    const store = firstText(order.store, order.storeName, order.raw?.store, order.raw?.storeName, order.site, order.raw?.site);
    const storeSignal = storeSignals.find((signal) => signal.store === store);
    map.set(order.id, predictLinkSla(order, {
      now: options.now,
      storeSignal,
      driverSignals,
      targetMinutes: options.targetMinutes,
    }));
  });

  return map;
}

export function summarizeLinkPredictions(predictionMap = new Map()) {
  const predictions = [...predictionMap.values()].filter((prediction) => prediction.currentRisk !== 'closed');
  return {
    total: predictions.length,
    predictedCritical: predictions.filter((prediction) => prediction.predictedRisk === 'critical').length,
    rising: predictions.filter((prediction) => prediction.riskTrend === 'rising').length,
    recoverable: predictions.filter((prediction) => prediction.recoveryChance >= 70).length,
  };
}
